import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  Users,
  BookOpen,
  MessageSquare,
  Mail,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  GraduationCap,
  UserCircle,
  CreditCard
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/logo.png";

const navItems = [
  { name: 'Dashboard',      path: '/student/dashboard', icon: LayoutDashboard },
  { name: 'My Classes',     path: '/student/classes',   icon: Calendar },
  { name: 'Schedule',       path: '/student/schedule',  icon: CalendarDays },
  { name: 'Open Classes',   path: '/student/available', icon: Users },
  { name: 'Activities',     path: '/student/activities', icon: BookOpen },
  { name: 'My Plan',        path: '/student/billing',   icon: CreditCard },
  { name: 'Messages',       path: '/student/messages',  icon: Mail },
  { name: 'Chat',           path: '/student/chat',      icon: MessageSquare },
];

export const StudentLayout = ({ children }) => {
  const { settings } = usePublicSettings();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const { user, logout, isApproved } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const siteName = settings.site_name || "Frankly Talking English";
  const userMenuRef = useRef(null);
  const visibleNavItems = isApproved
    ? navItems
    : navItems.filter((item) => item.path === '/student/chat');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  // Fetch notification counts every 30s
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/student/notifications/counts`);
        setNotifCount(res.data.total || 0);
      } catch (_) {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex">
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-gradient-to-b from-[#1A2E6E] to-[#0F1A3E] text-white flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex-shrink-0 flex items-center justify-between px-6 border-b border-white/10">
          <Link to="/student/dashboard" className="flex items-center gap-2">
            <img
              src={LOGO_URL}
              alt={siteName}
              className="h-10 w-auto brightness-0 invert"
            />
          </Link>
          <button
            className="lg:hidden text-white/70 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* User card */}
        <div className="flex-shrink-0 mx-4 mt-4 px-4 py-3 bg-white/10 rounded-xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#4CAF50] flex items-center justify-center">
              <GraduationCap size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/60">Student Portal</p>
            </div>
          </div>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1 mt-2 scrollbar-thin scrollbar-thumb-white/20">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`student-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`
                  flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200
                  ${isActive
                    ? 'bg-white/15 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }
                `}
              >
                <Icon size={18} className="flex-shrink-0" />
                <span className="font-medium text-sm truncate">
                  {!isApproved && item.path === '/student/chat' ? 'Support Chat' : item.name}
                </span>
              </Link>
            );
          })}
        </nav>

        {/* Footer link — always visible at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-white/10">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-2.5 text-white/60 hover:text-white transition-colors text-sm"
          >
            {"← Back to Website"}
          </Link>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-[#E8EAF0] flex items-center justify-between px-4 lg:px-8">
          <button
            className="lg:hidden p-2 text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg"
            onClick={() => setSidebarOpen(true)}
            data-testid="student-mobile-menu-btn"
          >
            <Menu size={24} />
          </button>

          <h1 className="hidden lg:block text-lg font-semibold text-[#1A2E6E]">
            Student Portal
          </h1>

          {!isApproved && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-[#F5A623]/10 text-[#F5A623] rounded-full text-sm font-medium">
              <span className="w-2 h-2 bg-[#F5A623] rounded-full animate-pulse" />
              Pending Approval
            </div>
          )}

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(isApproved ? '/student/messages' : '/student/chat')}
              className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg relative"
              aria-label="Notifications"
            >
              <Bell size={20} />
              {notifCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-[#D72B2B] text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                  {notifCount > 9 ? '9+' : notifCount}
                </span>
              )}
            </button>

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 hover:bg-[#1A2E6E]/5 rounded-lg transition-colors"
                data-testid="student-user-menu-btn"
              >
                <div className="w-8 h-8 rounded-full bg-[#4CAF50] flex items-center justify-center text-white font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'S'}
                </div>
                <span className="hidden sm:block text-sm font-medium text-[#1A2E6E]">
                  {user?.name || 'Student'}
                </span>
                <ChevronDown size={16} className="text-[#1A2E6E]/60" />
              </button>

              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-[#E8EAF0] py-2 z-50"
                >
                  <div className="px-4 py-2 border-b border-[#E8EAF0]">
                    <p className="text-sm font-medium text-[#1A2E6E]">{user?.name}</p>
                    <p className="text-xs text-[#1A2E6E]/60">{user?.email}</p>
                  </div>
                  {isApproved && (
                    <Link
                      to="/student/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors"
                    >
                      <UserCircle size={16} />
                      My Profile
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    data-testid="student-logout-btn"
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default StudentLayout;
